import { CommonModule } from '@angular/common';
import { Component, NO_ERRORS_SCHEMA, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule, WindowModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { MessageStates } from '../../common/constants.lib';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from '../../shared/logger.service';
import { UserDataService } from '../../users/user-data.service';
import { AuthTokenService } from '../auth-token.service';
import { confirmedPasswordValidator, firstOrLastNameRequiredValidator } from '../../directives/casi.validators';

@Component({
  selector: 'casi-reset-password-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    RouterLink, RouterLinkActive,
    DialogsModule,
    WindowModule,
    LabelModule, InputsModule,
    ButtonsModule
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './reset-password-view.html',
  styleUrl: './reset-password-view.scss'
})
export class ResetPasswordView implements OnInit {

  form: FormGroup;
  MessageStates = MessageStates;
  public user: any = {};

  email: string | null = null;
  firstName: string | null = null;
  lastName: string | null = null;
  researchConsent: string | null = null;
  newsLetter: string | null = null;
  resetToken: string | null = null;
  isNewUser: boolean | null = false;

  hasUser: boolean = false;
  fetchError: boolean = false;

  public updateSuccess: boolean = false;

  private _messageState = new BehaviorSubject<MessageStates>(MessageStates.Before);
  get messageState() {
    return this._messageState.getValue();
  }
  set messageState(val: MessageStates) {
    this._messageState.next(val);
  }

  constructor(
    private logger: LoggerService,
    private fb: FormBuilder,
    public faIcons: FaIconLibrary,
    private router: Router,
    private dataService: UserDataService,
    private activatedRoute: ActivatedRoute,
    private authTokenService: AuthTokenService
  ) {
    this.form = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(6)]],
        confirmedPassword: ['', [Validators.required]],
        first_name: [null],
        last_name: [null],
        research_consent: [true],
        newsletter: [true]
      },
      {
        validators: [
          confirmedPasswordValidator,
          firstOrLastNameRequiredValidator
        ]
      }
    );
  }

  //#region Form

  get f() {
    return this.form.controls;
  }

  get password() {
    return this.form.get('password');
  }

  get confirmedPassword() {
    return this.form.get('confirmedPassword');
  }

  get first_name() {
    return this.form.get('first_name');
  }
  get last_name() {
    return this.form.get('last_name');
  }

  get research_consent() {
    return this.form.get('research_consent');
  }

  get newsletter() {
    return this.form.get('newsletter');
  }

  //#endregion

  //#region Ng handlers

  ngOnInit(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      this.logger.debug('ngOnInit:query params: ', params);
      this.email = params.get('email');
      this.resetToken = params.get('resettoken');
      this.firstName = params.get('firstname');
      this.lastName = params.get('lastname');
      this.newsLetter = params.get('newsletter');
      this.researchConsent = params.get('researchconsent')
      this.logger.debug('ngOnInit:query params: ', {
        email: this.email,
        resetToken: this.resetToken,
        firstName: this.firstName,
        lastName: this.lastName,
        newsletter: this.newsLetter,
        reseachConsent: this.researchConsent
      });
      this.form.patchValue({
        first_name: this.firstName,
        last_name: this.lastName,
        research_consent: this.researchConsent ? this.researchConsent === "true" : true,
        newsletter: this.newsletter ? this.newsLetter === "true" : false
      });
      if (this.email && this.resetToken) {
        this.checkPassword(this.email, this.resetToken);
      }
    })
    this.setForm();
  }

  //#endregion

  //#region Public methods

  setForm() {
    this.messageState = MessageStates.Before; // 'before';
    // this.form.patchValue({ email: null });
  }

  onSubmit(): void {
    this.logger.debug('onSubmit...', this.form.value);
    const toSave = {
      ...this.form.value,
      email: this.user.email
    }
    this.dataService
      .setPassword(toSave)
      .then(resData => {
        this.logger.debug('onSubmit:resData: ', resData);
        this.authTokenService.setToken(resData.token);
        this.updateSuccess = true;
        this.reloadUser();
      })
      .catch(err => {
        this.logger.error('onSubmit: error: ', err);
      });
  }

  // onSubmit(): void {
  //   this.logger.debug('onSubmit...', this.form.value);
  //   const { password } = this.form.value;
  //   this.dataService
  //     .resetPassword(this.user.email, password)
  //     .then(resData => {
  //       this.logger.debug('onSubmit:resData: ', resData);
  //       this.authTokenService.setToken(resData.token);
  //       this.updateSuccess = true;
  //       this.reloadUser();
  //     })
  //     .catch(err => {
  //       this.logger.error('onSubmit: error: ', err);
  //     });
  // }

  checkPassword(email: string, resetToken: string) {
    this.logger.debug('checkPassword...', { email, resetToken });
    return this.dataService.checkPassword(email, resetToken)
      .then(user => {
        this.logger.debug('checkPassword:user: ', user);
        this.user = user;
      })
      .catch(err => {
        this.logger.error('checkPassword:error: ', err);
        this.user = null;
        this.fetchError = true;
      })
      .finally(() => {
        this.hasUser = !this.user;
      });
  }

  fetchTerms() {
    this.logger.debug('checkPassword...', this.form.value);

  }

  cancelPassword() {
    this.logger.debug(`cancelPassword...`);
    this.router.navigate(['/home']);
  }

  reloadUser(): void {
    this.logger.debug("reloadUser...");
    this.updateSuccess = false;
    this.dataService.checkTerms()
      .then((agreed) => {
        if (agreed) {
          this.router.navigate(['/home']);
        } else {
          this.router.navigate(['/terms-check']);
        }
      })
      .catch(err => {
        this.logger.error('reloadUser:error: ', err.message);
      })
  }

  //#endregion

}

